import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertsType } from '@rappi/ui/alerts';
import { TierService } from '../../../../services/tier.service';
import { ComponentService } from '../../../../services/component.service';

import { SelectComponent } from '@rappi/ui/select';
import { TreeComponent } from '@rappi/ui/tree';
import { Tier } from '../../../../definitions/tier.models';
import { AlertsService } from '../../../../../../app/services/alerts/alerts.service';
import { PermissionComponent } from '../../../../definitions/component.models';
import { ACTIONS } from '../../../../definitions/actions.enums';
import { MODULES_ICON } from '../../../../definitions/table-modules.enums';

import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../store/states/app.state';
import { StoreName } from '../../../../../store/definitions/store.constants';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Entity, Permission } from '../../../../definitions/permissions.models';
import { PermissionAction } from '../../../../definitions/permissions.enums';
import { ConfirmModalComponent } from '../../../../../core/confirm-modal/confirm-modal.component';
import { TypeModal } from '../../../../../core/confirm-modal/definitions/enums/type-modal.enum';
import { TypeCofirmModal } from '../../../../../core/confirm-modal/definitions/models/type-modal.model';
import { CreatePermissionDialogComponent } from '../create-permission-dialog/create-permission-dialog.component';
import { TreePermission } from '../../definitions/tiers.interface';
import { ConfirmCreatePermissionDialogComponent } from '../confirm-create-permission-dialog/confirm-create-permission-dialog.component';
import { DeletePermissionDialogComponent } from '../delete-permission-dialog/delete-permission-dialog.component';
import { HasPermissionsService } from '../../../../../shared/services/has-permissions/has-permissions.service';
@Component({
  selector: 'app-manager',
  templateUrl: './manager.component.html',
  styleUrls: ['./manager.component.scss']
})
export class ManagerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('typeSelect') typeSelect: SelectComponent<Entity>;
  @ViewChild('tree') tree: TreeComponent;
  action: string;
  form: FormGroup = new FormGroup({
    name: new FormControl(null, Validators.required),
    type: new FormControl(null, Validators.required),
    description: new FormControl(null, Validators.compose([Validators.required, Validators.maxLength(254)])),
    components_id: new FormControl([], Validators.required),
    test: new FormControl(false, Validators.required)
  });

  typeDataSource: Entity[] = [];

  id: number;

  deleteTier: (id: number) => Observable<Permission<unknown>>;
  components$: Observable<PermissionComponent[]>;

  viewUrl: string;
  tableUrl: string;

  contextMenuProcessed = false;
  destroySubject$: Subject<void> = new Subject();

  showMenu = false;
  menuX = 0;
  menuY = 0;
  component: TreePermission;

  private readonly TIER_CREATION_MESSAGE = 'Tier created successfully';
  private readonly TIER_CREATION_ERROR = 'There was an error trying to create tier';

  private readonly TIER_UPDATE_MESSAGE = 'Tier updated successfully';
  private readonly TIER_UPDATE_ERROR = 'There was an error trying to update tier with id';

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _router: Router,
    private readonly _tierService: TierService,
    private readonly _componentService: ComponentService,
    private readonly _alertsService: AlertsService,
    private readonly _store: Store<AppState>,
    private readonly _dialog: MatDialog,
    public readonly _hasPermissionService: HasPermissionsService
  ) {
    const routeArr = this._router.url.split('/');

    this.id = Number(routeArr[4]);
    this.action = routeArr[3];

    routeArr.length = 3;

    this.tableUrl = routeArr.join('/');
    this.viewUrl = this.id ? `${this.tableUrl}/${PermissionAction.view}/${this.id}` : null;
    this.deleteTier = (id: number) => this._tierService.deleteTier(id).pipe(tap(() => this.goTo(this.tableUrl)));

    this._store
      .pipe(
        select(StoreName.country),
        skip(1),
        takeUntil(this.destroySubject$),
        tap(() => {
          this.goTo(this.tableUrl);
        })
      )
      .subscribe();

    this.getTiersTypes();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (event.button === 2) {
      this.resetContextMenuProcessed();
    }
  }

  @HostListener('document:click')
  closeMenu() {
    this.showMenu = false;
  }

  ngAfterViewInit() {
    this.getComponents();

    this._cd.detectChanges();

    if (this.action === ACTIONS.edit) {
      this._tierService.getTier(this.id).subscribe(
        ({ data }: Permission<Tier>) => {
          this.form.patchValue({ ...data });

          this.typeSelect.setSelection({ id: data.type, name: data.type } as Entity);

          this.tree.setInitialSelection(data.components || []);

          this._cd.detectChanges();
        },
        ({ error: { message } }: HttpErrorResponse) => {
          this._alertsService.openAlerts(
            message || 'There was an error trying to get tier or components',
            AlertsType.error
          );
        }
      );
    }
  }

  getTiersTypes(): void {
    this._tierService
      .getTiersTypes()
      .pipe(map((response: { data: string[] }) => response.data.map((element) => ({ id: element, name: element }))))
      .subscribe((response) => (this.typeDataSource = response));
  }

  setType({ id }: Entity) {
    this.form.get('type').patchValue(id);
  }

  setComponents(selection: number[]) {
    this.form.get('components_id').patchValue(selection);
  }

  goTo(url: string) {
    this._router.navigateByUrl(url);
  }

  onSubmit(f: FormGroup) {
    if (f.invalid) {
      f.markAllAsTouched();
      return;
    }

    const request = f.getRawValue() as Tier;

    const action = this.id
      ? this.confirmEdit().pipe(
          filter((res: boolean) => res),
          switchMap(() => this._tierService.editTier(request, this.id))
        )
      : this._tierService.createTier(request);

    const MESSAGE: string = this.id ? this.TIER_UPDATE_MESSAGE : this.TIER_CREATION_MESSAGE;
    const ERROR: string = this.id ? `${this.TIER_UPDATE_ERROR} ${this.id}` : this.TIER_CREATION_ERROR;

    action.subscribe(
      ({ data: { id } }: Permission<Tier>) => {
        this._alertsService.openAlerts(MESSAGE, AlertsType.success);
        this.goTo(this.viewUrl || `${this.tableUrl}/${PermissionAction.view}/${id}`);
      },
      ({ error: { message } }: HttpErrorResponse) => {
        this._alertsService.openAlerts(message || ERROR, AlertsType.error);
      }
    );
  }

  confirmEdit(): Observable<boolean> {
    return this._dialog
      .open(ConfirmModalComponent, {
        disableClose: true,
        width: '500px',
        autoFocus: false,
        data: {
          title: 'Confirm Edit',
          message: `The licenses and roles associated to this Tier
                    will be modified or even eliminated due to the
                    current changes, do you still want to continue?`,
          type: TypeModal.confirm
        } as TypeCofirmModal
      })
      .afterClosed();
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  onRightClick(event: { event: MouseEvent; node: TreePermission }) {
    if (!this.contextMenuProcessed) {
      this.contextMenuProcessed = true;
      this.menuX = event.event.clientX;
      this.menuY = event.event.clientY;
      this.showMenu = true;
      this.component = event.node;
    }
  }

  resetContextMenuProcessed() {
    this.contextMenuProcessed = false;
  }

  onOptionClick(option: string) {
    this.showMenu = false;

    if (option === 'Create') {
      this.openCreatePermissionDialog(true);
    }

    if (option === 'Delete') {
      this.openDeletePermissionDialog();
    }
  }

  openCreatePermissionDialog(isChild: boolean) {
    const dialogRef = this._dialog.open(CreatePermissionDialogComponent, {
      maxWidth: '80%',
      width: '650px',
      disableClose: true,
      data: { isPermissionChild: isChild, permission: this.component }
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((v) => !!v),
        switchMap((result: { action: string; value: string }) => {
          if (result.action === 'create') {
            return this._tierService.createPermission(this.component, result.value, isChild).pipe(
              catchError((error: HttpErrorResponse) => {
                this._alertsService.openAlerts(
                  error.message || 'There was an error trying to create the permission',
                  AlertsType.error
                );
                return throwError(error);
              })
            );
          } else {
            return EMPTY;
          }
        }),
        takeUntil(this.destroySubject$)
      )
      .subscribe(() => {
        this.openConfirmModal('create');
      });
  }

  openConfirmModal(action: string) {
    this._dialog
      .open(ConfirmCreatePermissionDialogComponent, {
        maxWidth: '80%',
        width: '650px',
        disableClose: true,
        data: { action }
      })
      .afterClosed()
      .subscribe(() => this.getComponents());
  }

  openDeletePermissionDialog() {
    const dialogRef = this._dialog.open(DeletePermissionDialogComponent, {
      maxWidth: '80%',
      width: '650px',
      disableClose: true,
      data: { permission: this.component }
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result: string) => result === 'delete'),
        switchMap(() =>
          this._tierService.deletePermission(this.component.id).pipe(
            catchError((error: HttpErrorResponse) => {
              this._alertsService.openAlerts(
                error.message || 'There was an error trying to delete the permission',
                AlertsType.error
              );
              return throwError(error);
            })
          )
        ),
        takeUntil(this.destroySubject$)
      )
      .subscribe(() => {
        this.openConfirmModal('delete');
      });
  }

  getComponents(): void {
    this.components$ = this._componentService.getComponents().pipe(
      map(({ data }: Permission<PermissionComponent[]>) =>
        data.map((d: PermissionComponent) => {
          d.class = MODULES_ICON[d.name];
          return d;
        })
      )
    );
  }
}
